import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Fallback timezone when an event has no `timezone` set. Matches the app config
 * timezone in `packages/stack9-stack/src/app.json`.
 */
export const DEFAULT_TIMEZONE = 'Australia/Sydney';

export const DEFAULT_SLOT_DATETIME_FORMAT = 'DD/MM/YYYY HH:mm';

/**
 * Mirrors `packages/stack9-stack/src/utils/datetime.ts` `formatDate`, with one
 * deliberate difference: when a `tz` is given the value is read UTC-first
 * (`dayjs.utc(value)`) before converting. Stored instants are true UTC values
 * (see `SLOT_TZ_ISSUE.md`), and being explicit about that is the point.
 */
export function formatDate(
  value?: string | Date | null,
  options?: { format?: string; tz?: string | null },
): string {
  if (!value) return '';
  const { format, tz } = options ?? {};
  return (tz ? dayjs.utc(value).tz(tz) : dayjs(value)).format(
    format ?? 'DD/MM/YYYY',
  );
}

/**
 * Format a stored slot instant as the intended wall-clock time in the event's
 * timezone.
 *
 * Appointment slots are stored as true UTC instants anchored to the event's own
 * timezone (see `generateAppointmentSlots.ts` and `SLOT_TZ_ISSUE.md`). To show
 * the intended local time we must convert the instant back into the event's
 * `timezone`. The browser's local zone — what a bare `dayjs(value)` uses — would
 * display the wrong wall-clock for any admin not physically in the event's
 * timezone, which is the whole bug this guards against.
 *
 * Assumes the slot backfill has run, so every stored instant is correctly
 * encoded; no legacy (Sydney-encoded) reinterpretation is applied.
 */
export function formatSlotDateTime(
  value?: string | Date | null,
  timezone?: string | null,
): string {
  return formatDate(value, {
    tz: timezone || DEFAULT_TIMEZONE,
    format: DEFAULT_SLOT_DATETIME_FORMAT,
  });
}

export { dayjs };
